import { QuestionModal } from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/components/OverviewTab/components/QuestionModal";
import LocaleChipSelect from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/components/OverviewTab/components/QuestionModal/LocaleChipSelect";
import { type QuestionModalConfirmArg } from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/components/OverviewTab/types";
import { questionnaireLocaleAtom } from "@app/screens/ExternalQuestionnaires/externalQuestionnaire/ctx";
import { Button } from "@design-system/Inputs/Button";
import SearchBar from "@design-system/Inputs/SearchBar";
import { Dropdown } from "@design-system/Inputs/UniversalDropdown";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { type QuestionType } from "@generated/client/graphql";
import { useAtom } from "jotai";
import { useEffect, useState, type FC } from "react";
import { useQueryParam } from "use-query-params";
import { filterParamArgs } from "../../ctx";
import {
    useCreateTemplateQuestion,
    useExportTemplateQuestions,
    useSupplierTemplateQuestionnaireLocale,
} from "../../data";
import { makeTemplateQuestionCreateInput } from "../../services";
import { type QuestionFilter, type SupplierQuestionnaire } from "../../types";
import { CreateQuestionnaireFromUploadModal } from "../CreateQuestionsFromUploadModal";
import { UpdateQuestionnaireModal } from "../QuestionnaireModal";
import { QuestionSortableList } from "../QuestionSortableList";
import { useSupplierQuestionsHeaderTranslations } from "./i18n";
import { FilterChips } from "./QuestionFilterChips";

export const QuestionsHeader: FC<{
    templateQuestionnaire?: SupplierQuestionnaire | null;
    filters: QuestionFilter;
    onFiltersChange: (filters: QuestionFilter) => void;
}> = ({ templateQuestionnaire, filters, onFiltersChange }) => {
    const [search, setSearch] = useQueryParam(...filterParamArgs.search);
    const [answerTypes, setAnswerTypes] = useState<QuestionType[]>(
        filters.types,
    );
    const [tagIds, setTagIds] = useState<string[]>(filters.tagIds);
    const [questionGroupIds, setQuestionGroupIds] = useState<string[]>(
        filters.questionGroupIds,
    );
    const [currentLocale, setCurrentLocale] = useAtom(questionnaireLocaleAtom);

    const [isQuestionModalOpen, setIsQuestionModalOpen] = useState(false);

    const [
        isCreateQuestionnaireFromUploadModalOpen,
        setIsCreateQuestionnaireFromUploadModalOpen,
    ] = useState(false);

    const [isQuestionSortableListOpen, setIsQuestionSortableListOpen] =
        useState(false);

    const { availableLocales, defaultLocale } =
        useSupplierTemplateQuestionnaireLocale();
    const openCreateQuestionModal = () => {
        setIsQuestionModalOpen(true);
    };

    const { createQuestion, isPending: isCreateQuestionPending } =
        useCreateTemplateQuestion();

    const { exportTemplateQuestions } = useExportTemplateQuestions();

    const handleConfirm = (question: QuestionModalConfirmArg) => {
        const input = makeTemplateQuestionCreateInput(question);
        createQuestion(input);
    };

    const handleAnswerTypeChange = (value: QuestionType[]) => {
        setAnswerTypes(value);
        onFiltersChange({ ...filters, types: value });
    };

    const handleTagChange = (value: string[]) => {
        setTagIds(value);
        onFiltersChange({ ...filters, tagIds: value });
    };

    const handleQuestionGroupChange = (value: string[]) => {
        setQuestionGroupIds(value);
        onFiltersChange({ ...filters, questionGroupIds: value });
    };

    const handleExportQuestions = () => {
        exportTemplateQuestions(undefined);
    };

    const handleImportQuestions = () => {
        setIsCreateQuestionnaireFromUploadModalOpen(true);
    };

    const handleSortQuestions = () => {
        setIsQuestionSortableListOpen(true);
    };

    useEffect(() => {
        setCurrentLocale(defaultLocale);
    }, [availableLocales, defaultLocale]);

    const { t, keys } = useSupplierQuestionsHeaderTranslations();

    if (!availableLocales?.length || !defaultLocale || !currentLocale)
        return null;

    return (
        <FlexCol gap="4">
            <FlexRow gap="2" alignItems="center">
                <FlexRow gap="2">
                    <UpdateQuestionnaireModal
                        questionnaire={{
                            availableLocales,
                            defaultLocale,
                        }}
                    />
                    <LocaleChipSelect
                        locale={currentLocale}
                        setLocale={setCurrentLocale}
                        availableLocales={availableLocales}
                    />
                </FlexRow>
                <FilterChips>
                    <FilterChips.AnswerTypeFilterChip
                        value={answerTypes}
                        onValueChange={handleAnswerTypeChange}
                    />
                    <FilterChips.TagFilterChip
                        value={tagIds}
                        onValueChange={handleTagChange}
                    />
                    <FilterChips.QuestionGroupFilterChip
                        value={questionGroupIds}
                        onValueChange={handleQuestionGroupChange}
                        locale={currentLocale}
                    />
                </FilterChips>
            </FlexRow>
            <FlexRow gap="2" justifyContent="between">
                <SearchBar
                    searchString={search}
                    setSearchString={setSearch}
                    placeholder={t(keys.search)}
                    className="min-w-[200px]"
                />
                {templateQuestionnaire?.id && (
                    <FlexRow gap="2">
                        <Button
                            variant="flatPrimary"
                            size="sm"
                            onClick={openCreateQuestionModal}
                        >
                            <Button.Icon name="plus" />
                            {t(keys.add_question_btn)}
                        </Button>
                        <Dropdown>
                            <Dropdown.Trigger asChild>
                                <Button variant="outlined" size="sm">
                                    <Button.Icon name="more" />
                                </Button>
                            </Dropdown.Trigger>
                            <Dropdown.Content hasPortal>
                                <Dropdown.Item onClick={handleImportQuestions}>
                                    <Dropdown.ItemIcon name="arrowUpload" />
                                    {t(keys.import_questions)}
                                </Dropdown.Item>
                                <Dropdown.Item onClick={handleExportQuestions}>
                                    <Dropdown.ItemIcon name="arrowDownload" />
                                    {t(keys.exportQuestions)}
                                </Dropdown.Item>
                                <Dropdown.Item onClick={handleSortQuestions}>
                                    <Dropdown.ItemIcon name="list" />
                                    {t(keys.sort_questions)}
                                </Dropdown.Item>
                            </Dropdown.Content>
                        </Dropdown>
                        <QuestionModal
                            open={isQuestionModalOpen}
                            hasKeepOpenOnConfirmOption
                            setOpen={setIsQuestionModalOpen}
                            loading={isCreateQuestionPending}
                            onConfirm={handleConfirm}
                            questionnaireId={templateQuestionnaire?.id}
                            withNa
                            withProof
                        />
                        <CreateQuestionnaireFromUploadModal
                            open={isCreateQuestionnaireFromUploadModalOpen}
                            onOpenChange={
                                setIsCreateQuestionnaireFromUploadModalOpen
                            }
                        />
                        <QuestionSortableList
                            open={isQuestionSortableListOpen}
                            onOpenChange={setIsQuestionSortableListOpen}
                        />
                    </FlexRow>
                )}
            </FlexRow>
        </FlexCol>
    );
};
